import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Section, Cell, Caption, Button, Textarea, Badge } from '@telegram-apps/telegram-ui'
import { CheckCircle2, Circle, Briefcase, Home, Check } from 'lucide-react'

import { api } from '../../lib/api'
import type { Task, Comment } from '../../lib/types'
import { formatDeadline } from '../../lib/format'
import BackHeader from '../../components/BackHeader'
import { SkeletonList } from '../../components/Skeleton'
import { hapticSuccess } from '../../lib/telegram'

interface TaskDetailResp {
  task: Task
  comments: Comment[]
}

/** Детали поручения для исполнителя (Анастасии или Владимира). */
export default function AssigneeTaskDetail() {
  const { id } = useParams<{ id: string }>()
  const taskId = Number(id)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [comment, setComment] = useState('')

  const { data, isLoading } = useQuery<TaskDetailResp>({
    queryKey: ['task', taskId],
    queryFn: () => api.get<TaskDetailResp>(`/tasks/${taskId}`),
  })

  const markDone = useMutation({
    mutationFn: () => api.post(`/tasks/${taskId}/done`),
    onSuccess: () => {
      hapticSuccess()
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['my-calendar'] })
      navigate(-1)
    },
  })

  const addComment = useMutation({
    mutationFn: () => api.post(`/tasks/${taskId}/comments`, { content: comment }),
    onSuccess: () => {
      setComment('')
      qc.invalidateQueries({ queryKey: ['task', taskId] })
    },
  })

  if (isLoading || !data) {
    return (
      <>
        <BackHeader title="Поручение" />
        <SkeletonList count={4} />
      </>
    )
  }

  const t = data.task
  const isDone = t.status === 'done'

  return (
    <>
      <BackHeader title={t.title} />

      <Section>
        <Cell
          before={isDone
            ? <CheckCircle2 size={20} color="var(--success)" />
            : <Circle size={20} color="var(--warning)" />}
          subtitle={<Caption>{isDone ? 'Выполнено' : formatDeadline(t.deadline)}</Caption>}
        >
          <Badge type="number">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {t.category === 'work' ? <Briefcase size={12} /> : <Home size={12} />}
              {t.category === 'work' ? 'Рабочее' : 'Личное'}
            </span>
          </Badge>
        </Cell>

        {t.description && (
          <Cell multiline subtitle="Описание">{t.description}</Cell>
        )}

        <Cell subtitle="Поставил">Александр Юрьевич</Cell>
      </Section>

      <Section header={`Комментарии (${data.comments.length})`}>
        {data.comments.length === 0 ? (
          <Cell><Caption style={{ opacity: 0.6 }}>Пока пусто</Caption></Cell>
        ) : (
          data.comments.map((c) => (
            <Cell
              key={c.id}
              subtitle={<Caption>{c.author_role === 'director' ? 'Александр Юрьевич' : 'Я'} · {new Date(c.created_at).toLocaleString('ru-RU')}</Caption>}
              multiline
            >
              {c.content}
            </Cell>
          ))
        )}

        <div style={{ padding: '12px 16px' }}>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Например: уточнить адрес у клиента"
          />
          <div style={{ marginTop: 8 }}>
            <Button stretched disabled={!comment.trim() || addComment.isPending} onClick={() => addComment.mutate()}>
              Отправить
            </Button>
          </div>
        </div>
      </Section>

      {!isDone && (
        <div style={{ padding: 16 }}>
          <Button stretched size="l" onClick={() => markDone.mutate()} before={<Check size={18} />}>
            Выполнено
          </Button>
        </div>
      )}
    </>
  )
}
